import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Subscription } from 'rxjs';

import {
  OsintAiAnalysisResponse,
  OsintSearchService,
} from '../../../services/osint-search/osint-search.service';
import { SearchServiceStatusService } from '../../../services/search-service-status/search-service-status.service';

@Component({
  selector: 'app-osint-ai-analysis',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  templateUrl: './osint-ai-analysis.component.html',
  styleUrl: './osint-ai-analysis.component.css',
})
export class OsintAiAnalysisComponent implements OnChanges, OnDestroy {
  private readonly urlPattern: RegExp = /https?:\/\/[^\s)]+/i;
  private readonly pollIntervalMs = 5000;
  private readonly maxPollAttempts = 18;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private pollAttempts = 0;
  private creditCostsSubscription: Subscription | null = null;

  @Input() searchId: string = '';
  @Input() type: string = '';
  @Input() queryValue: string = '';
  @Input() hasSourceData: boolean = true;

  analysisResponse: OsintAiAnalysisResponse | null = null;
  isLoading: boolean = false;
  isGenerating: boolean = false;
  isPolling: boolean = false;
  errorMessage: string = '';
  analysisMode: 'quick' | 'deep' | 'deep-cheap' = 'quick';
  feedbackStatus: 'wrong' | 'correct' | 'needs-review' | '' = '';
  feedbackNote: string = '';
  creditCosts: Record<string, number> | null = null;

  constructor(
    private osintSearchService: OsintSearchService,
    private searchServiceStatusService: SearchServiceStatusService,
  ) {
    this.creditCostsSubscription = this.searchServiceStatusService.creditCosts$.subscribe((creditCosts) => {
      this.creditCosts = creditCosts;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchId'] || changes['type'] || changes['hasSourceData']) {
      this.stopPolling();
      this.errorMessage = '';
      this.analysisResponse = null;
      this.resetFeedbackDraft();

      if (!this.searchId || !this.type || !this.hasSourceData) {
        return;
      }

      this.loadAnalysis();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.creditCostsSubscription?.unsubscribe();
  }

  get hasAnalysis(): boolean {
    return Boolean(this.analysisResponse?.analysis);
  }

  get confidenceClass(): string {
    const confidence = this.analysisResponse?.analysis?.confidence || 'low';
    return `confidence-${confidence}`;
  }

  get isDeepMode(): boolean {
    return this.analysisMode === 'deep' || this.analysisMode === 'deep-cheap';
  }

  get canUseFeedback(): boolean {
    return this.isDeepMode;
  }

  get isDeepCheapMode(): boolean {
    return this.analysisMode === 'deep-cheap';
  }

  get savedFeedback(): { status?: string | null; note?: string | null; updated_at?: string | null } | null {
    return this.analysisResponse?.analyst_feedback || null;
  }

  get currentModeCreditCost(): number | null {
    const key =
      this.analysisMode === 'deep'
        ? 'aiAnalysisDeepCreditCost'
        : this.analysisMode === 'deep-cheap'
          ? 'aiAnalysisDeepCheapCreditCost'
          : 'aiAnalysisQuickCreditCost';

    return this.searchServiceStatusService.resolveCreditCost(this.creditCosts, key);
  }

  get regenerateWithFeedbackCostLabel(): string {
    const cost = this.currentModeCreditCost;
    if (typeof cost === 'number' && Number.isFinite(cost)) {
      return `${cost} credit${cost === 1 ? '' : 's'}`;
    }

    return 'credits for this mode';
  }

  get primarySubjectConfidenceClass(): string {
    const confidence =
      this.analysisResponse?.analysis?.primarySubject?.confidence || 'low';
    return `confidence-${confidence}`;
  }

  get attributionLabel(): string {
    const status =
      this.analysisResponse?.analysis?.primarySubject?.attributionStatus ||
      this.analysisResponse?.analysis?.attributionStatus ||
      '';

    switch (status) {
      case 'confirmed':
        return 'Confirmed';
      case 'strong candidate':
        return 'Possible candidate';
      case 'supporting related profile':
        return 'Supporting profile';
      default:
        return 'Weak mention';
    }
  }

  get attributionClass(): string {
    const status =
      this.analysisResponse?.analysis?.primarySubject?.attributionStatus ||
      this.analysisResponse?.analysis?.attributionStatus ||
      '';

    switch (status) {
      case 'confirmed':
        return 'attribution-confirmed';
      case 'strong candidate':
        return 'attribution-candidate';
      case 'supporting related profile':
        return 'attribution-supporting';
      default:
        return 'attribution-weak';
    }
  }

  get contradictionFlags(): string[] {
    return this.analysisResponse?.analysis?.contradictionFlags || [];
  }

  get confirmedEvidenceBlocks(): any[] {
    return this.analysisResponse?.analysis?.confirmedEvidence || [];
  }

  get strongCandidateProfiles(): any[] {
    return this.analysisResponse?.analysis?.strongCandidateProfiles || [];
  }

  get supportingRelatedProfiles(): any[] {
    return this.analysisResponse?.analysis?.supportingRelatedProfiles || [];
  }

  get officialProfessionalEvidence(): any[] {
    return this.analysisResponse?.analysis?.officialProfessionalEvidence || [];
  }

  get publicSocialEvidence(): any[] {
    return this.analysisResponse?.analysis?.publicSocialEvidence || [];
  }

  get crossPlatformCorroboration(): string[] {
    return this.analysisResponse?.analysis?.crossPlatformCorroboration || [];
  }

  get limitations(): string[] {
    return this.analysisResponse?.analysis?.limitations || [];
  }

  get socialProfilesCount(): number {
    return this.analysisResponse?.analysis?.socialProfiles?.length || 0;
  }

  get displaySubjectName(): string {
    return (
      this.analysisResponse?.analysis?.primarySubject?.displayName ||
      this.analysisResponse?.analysis?.headline ||
      this.queryValue ||
      'Subject'
    );
  }

  get displaySubjectSummary(): string {
    return (
      this.analysisResponse?.analysis?.executiveSummary ||
      this.analysisResponse?.analysis?.primarySubject?.description ||
      ''
    );
  }

  get heroSummaryPoints(): string[] {
    const description = this.analysisResponse?.analysis?.primarySubject?.description?.trim();
    if (description) {
      return this.splitTextToBulletPoints(description);
    }

    return this.splitTextToBulletPoints(this.displaySubjectSummary);
  }

  get executiveSummaryPoints(): string[] {
    return this.splitTextToBulletPoints(
      this.analysisResponse?.analysis?.executiveSummary || '',
    );
  }

  splitTextToBulletPoints(text: string): string[] {
    const normalized = String(text || '').trim();
    if (!normalized) {
      return [];
    }

    const chunks = normalized
      .split(/(?:\.\s+|\n+|;\s+(?=[A-Z0-9+@]))/)
      .map((chunk) => chunk.trim().replace(/[.;]+$/, ''))
      .filter(Boolean);

    if (chunks.length <= 1) {
      return [this.formatBulletPoint(normalized)];
    }

    return chunks.map((chunk) => this.formatBulletPoint(chunk));
  }

  getPointAccentClass(index: number): string {
    const accents = [
      'point-accent-cyan',
      'point-accent-green',
      'point-accent-purple',
      'point-accent-orange',
      'point-accent-blue',
    ];
    return accents[index % accents.length];
  }

  get activeModeLabel(): string {
    const mode = this.analysisResponse?.analysis_mode || this.analysisMode;
    if (mode === 'deep-cheap') {
      return 'Deep cheap';
    }
    if (mode === 'deep') {
      return 'Deep';
    }
    return 'Quick';
  }

  get liveWebLabel(): string {
    return this.analysisResponse?.live_web_used ? 'Yes' : 'No';
  }

  get liveWebClass(): string {
    return this.analysisResponse?.live_web_used ? 'stat-positive' : 'stat-negative';
  }

  getSubjectInitials(name?: string): string {
    const source = String(name || this.displaySubjectName || '?').trim();
    if (!source) {
      return '?';
    }

    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
    }

    return source.slice(0, 2).toUpperCase();
  }

  getEntityIcon(type: string): string {
    switch (String(type || '').toLowerCase()) {
      case 'phone':
        return 'phone';
      case 'email':
        return 'mail';
      case 'company':
        return 'business';
      case 'address':
        return 'home';
      case 'location':
        return 'place';
      case 'username':
        return 'person';
      case 'wallet':
        return 'account_balance_wallet';
      case 'platform':
        return 'public';
      default:
        return 'link';
    }
  }

  getEntityAccentClass(type: string, index: number): string {
    const accents = ['entity-accent-purple', 'entity-accent-green', 'entity-accent-blue', 'entity-accent-cyan'];
    const typeMap: Record<string, string> = {
      phone: 'entity-accent-purple',
      email: 'entity-accent-green',
      company: 'entity-accent-blue',
      address: 'entity-accent-cyan',
      location: 'entity-accent-cyan',
    };
    return typeMap[String(type || '').toLowerCase()] || accents[index % accents.length];
  }

  isPrimaryIdentityCandidate(candidate: { confidence?: string; attributionStatus?: string }, index: number): boolean {
    if (index === 0) {
      return true;
    }

    const status = String(candidate?.attributionStatus || '').toLowerCase();
    return status === 'confirmed' || candidate?.confidence === 'high';
  }

  getPlatformBrandIconClass(platform: string): string {
    return this.resolvePlatformBrand(platform).iconClass;
  }

  getPlatformBrandShadeClass(platform: string): string {
    return this.resolvePlatformBrand(platform).shadeClass;
  }

  private resolvePlatformBrand(platform: string): { iconClass: string; shadeClass: string } {
    const key = String(platform || '').toLowerCase().trim();

    if (key.includes('linkedin')) {
      return { iconClass: 'fab fa-linkedin-in', shadeClass: 'platform-shade-linkedin' };
    }
    if (key.includes('facebook')) {
      return { iconClass: 'fab fa-facebook-f', shadeClass: 'platform-shade-facebook' };
    }
    if (key.includes('instagram')) {
      return { iconClass: 'fab fa-instagram', shadeClass: 'platform-shade-instagram' };
    }
    if (
      key.includes('twitter') ||
      key === 'x' ||
      key.includes('x (twitter)') ||
      key.includes('x.com') ||
      /^x\s*\(/.test(key)
    ) {
      return { iconClass: 'fab fa-x-twitter', shadeClass: 'platform-shade-x' };
    }
    if (key.includes('whatsapp')) {
      return { iconClass: 'fab fa-whatsapp', shadeClass: 'platform-shade-whatsapp' };
    }
    if (key.includes('telegram')) {
      return { iconClass: 'fab fa-telegram', shadeClass: 'platform-shade-telegram' };
    }
    if (key.includes('youtube')) {
      return { iconClass: 'fab fa-youtube', shadeClass: 'platform-shade-youtube' };
    }
    if (key.includes('github')) {
      return { iconClass: 'fab fa-github', shadeClass: 'platform-shade-github' };
    }
    if (key.includes('tiktok')) {
      return { iconClass: 'fab fa-tiktok', shadeClass: 'platform-shade-tiktok' };
    }
    if (key.includes('snapchat')) {
      return { iconClass: 'fab fa-snapchat-ghost', shadeClass: 'platform-shade-snapchat' };
    }
    if (key.includes('pinterest')) {
      return { iconClass: 'fab fa-pinterest-p', shadeClass: 'platform-shade-pinterest' };
    }
    if (key.includes('reddit')) {
      return { iconClass: 'fab fa-reddit-alien', shadeClass: 'platform-shade-reddit' };
    }
    if (key.includes('threads')) {
      return { iconClass: 'fab fa-threads', shadeClass: 'platform-shade-threads' };
    }
    if (key.includes('microsoft')) {
      return { iconClass: 'fab fa-microsoft', shadeClass: 'platform-shade-microsoft' };
    }
    if (key.includes('truecaller')) {
      return { iconClass: 'fas fa-phone', shadeClass: 'platform-shade-phone' };
    }

    return { iconClass: 'fas fa-globe', shadeClass: 'platform-shade-default' };
  }

  getConfidenceShadeClass(confidence?: string): string {
    switch (String(confidence || 'low').toLowerCase()) {
      case 'high':
        return 'status-shade-high';
      case 'medium':
        return 'status-shade-medium';
      default:
        return 'status-shade-low';
    }
  }

  getAttributionShadeClass(attributionStatus?: string): string {
    switch (String(attributionStatus || '').toLowerCase().trim()) {
      case 'confirmed':
        return 'status-shade-confirmed';
      case 'strong candidate':
        return 'status-shade-candidate';
      case 'supporting related profile':
        return 'status-shade-supporting';
      case 'weak mention':
        return 'status-shade-weak';
      default:
        return '';
    }
  }

  getCardShadeClass(confidence?: string, attributionStatus?: string): string {
    const attributionShade = this.getAttributionShadeClass(attributionStatus);
    if (attributionShade) {
      return attributionShade;
    }

    return this.getConfidenceShadeClass(confidence);
  }

  get profileHeroShadeClass(): string {
    return this.getCardShadeClass(
      this.analysisResponse?.analysis?.confidence,
      this.analysisResponse?.analysis?.primarySubject?.attributionStatus ||
        this.analysisResponse?.analysis?.attributionStatus,
    );
  }

  getSavedFeedbackStatusLabel(status?: string | null): string {
    switch (status) {
      case 'wrong':
        return 'Wrong result';
      case 'correct':
        return 'Correct';
      case 'needs-review':
        return 'Needs revision';
      default:
        return 'Note only';
    }
  }

  getSavedFeedbackShadeClass(status?: string | null): string {
    switch (status) {
      case 'wrong':
        return 'feedback-saved-wrong';
      case 'correct':
        return 'feedback-saved-correct';
      case 'needs-review':
        return 'feedback-saved-review';
      default:
        return 'feedback-saved-note';
    }
  }

  getSavedFeedbackPillClass(status?: string | null): string {
    switch (status) {
      case 'wrong':
        return 'feedback-pill-wrong';
      case 'correct':
        return 'feedback-pill-correct';
      case 'needs-review':
        return 'feedback-pill-review';
      default:
        return 'feedback-pill-note';
    }
  }

  getEvidenceTierLabel(tier?: string): string {
    switch (tier) {
      case 'tier1':
        return 'Official / direct';
      case 'tier2':
        return 'Professional-public';
      case 'tier3':
        return 'Public social';
      default:
        return 'Supporting evidence';
    }
  }

  getProfileRoleLabel(role?: string): string {
    switch (role) {
      case 'confirmed':
        return 'Confirmed';
      case 'strong candidate':
        return 'Strong candidate';
      case 'supporting related profile':
        return 'Supporting profile';
      default:
        return 'Weak mention';
    }
  }

  getSocialProfileConfidenceClass(
    confidence: 'low' | 'medium' | 'high' | string,
  ): string {
    return `confidence-${confidence || 'low'}`;
  }

  getDisplayHandle(handle: string, platform: string): string {
    const normalizedHandle = String(handle || '').trim();
    if (!normalizedHandle) {
      return platform;
    }

    if (normalizedHandle.startsWith('@')) {
      return normalizedHandle;
    }

    return `@${normalizedHandle}`;
  }

  loadAnalysis(): void {
    this.stopPolling();
    this.isLoading = true;
    this.errorMessage = '';

    this.osintSearchService.getAiAnalysis(this.type, this.searchId, this.analysisMode).subscribe({
      next: (response) => {
        this.analysisResponse = response?.data || null;
        this.errorMessage = this.analysisResponse?.error || '';
        this.syncFeedbackDraftFromResponse();
        this.isLoading = false;
      },
      error: () => {
        this.errorMessage = 'Unable to load AI analysis right now.';
        this.isLoading = false;
      },
    });
  }

  generateAnalysis(useFeedback: boolean = false): void {
    if (!this.searchId || !this.type || this.isGenerating || this.isPolling) {
      return;
    }

    this.stopPolling();
    this.isGenerating = true;
    this.errorMessage = '';

    const payload =
      useFeedback && this.canUseFeedback
        ? {
            feedback_status: this.feedbackStatus || undefined,
            feedback_note: this.feedbackNote.trim() || undefined,
          }
        : {};

    this.osintSearchService.generateAiAnalysis(this.type, this.searchId, this.analysisMode, payload).subscribe({
      next: (response) => {
        this.analysisResponse = response?.data || null;
        this.errorMessage = this.analysisResponse?.error || '';
        this.syncFeedbackDraftFromResponse();
        this.isGenerating = false;
      },
      error: (error) => {
        if (this.shouldStartPolling(error)) {
          this.startPolling();
          return;
        }

        this.errorMessage =
          error?.error?.message_key ||
          'Unable to generate AI analysis right now.';
        this.isGenerating = false;
      },
    });
  }

  setAnalysisMode(mode: 'quick' | 'deep' | 'deep-cheap'): void {
    if (this.analysisMode === mode || this.isLoading || this.isGenerating || this.isPolling) {
      return;
    }

    this.stopPolling();
    this.analysisMode = mode;
    this.analysisResponse = null;
    this.errorMessage = '';
    this.resetFeedbackDraft();

    if (this.searchId && this.type && this.hasSourceData) {
      this.loadAnalysis();
    }
  }

  setFeedbackStatus(status: 'wrong' | 'correct' | 'needs-review'): void {
    if (!this.canUseFeedback || this.isLoading || this.isGenerating || this.isPolling) {
      return;
    }

    this.feedbackStatus = this.feedbackStatus === status ? '' : status;
  }

  updateFeedbackNote(value: string): void {
    this.feedbackNote = value;
  }

  clearFeedbackDraft(): void {
    if (this.isLoading || this.isGenerating || this.isPolling) {
      return;
    }

    this.resetFeedbackDraft();
  }

  getEntityLabel(type: string): string {
    return String(type || '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  getPlatformTitle(platform: string): string {
    const normalized = String(platform || '').trim();
    if (!normalized) {
      return 'Profile';
    }

    return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  isProfileEvidence(item: any): boolean {
    return Boolean(
      item?.platform ||
      item?.handle ||
      item?.profileUrl ||
      item?.profileType ||
      item?.biography,
    );
  }

  getEvidenceTitle(item: any, index: number): string {
    if (this.isProfileEvidence(item)) {
      return (
        item?.sectionTitle ||
        `${index + 1}. ${this.getPlatformTitle(item?.platform)} Account - ${this.getDisplayHandle(item?.handle, item?.platform)}`
      );
    }

    return item?.sectionTitle || item?.value || `Evidence ${index + 1}`;
  }

  getEvidenceSummary(item: any): string {
    if (!item) {
      return '';
    }

    return item?.summary || item?.profileAssessment || item?.contentThemeSummary || item?.value || '';
  }

  getSourceUrl(source: string): string {
    const normalized = String(source || '').trim();
    const match = normalized.match(this.urlPattern);
    return match?.[0] || '';
  }

  getSourceLabel(source: string): string {
    const normalized = String(source || '').trim();
    const sourceUrl = this.getSourceUrl(normalized);

    if (!sourceUrl) {
      return normalized;
    }

    if (normalized === sourceUrl) {
      return sourceUrl;
    }

    return normalized.replace(sourceUrl, '').replace(/[:\s-]+$/, '').trim();
  }

  private formatBulletPoint(value: string): string {
    const trimmed = String(value || '').trim().replace(/[.;]+$/, '');
    if (!trimmed) {
      return '';
    }

    return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
  }

  private shouldStartPolling(error: any): boolean {
    const statusCode = Number(error?.status || error?.error?.error_code || 0);
    return [0, 502, 503, 504, 524].includes(statusCode);
  }

  private startPolling(): void {
    this.isGenerating = false;
    this.isPolling = true;
    this.errorMessage = '';
    this.pollAttempts = 0;
    this.scheduleNextPoll(1500);
  }

  private scheduleNextPoll(delayMs: number = this.pollIntervalMs): void {
    this.clearPollTimer();
    this.pollTimer = setTimeout(() => {
      this.pollForGeneratedAnalysis();
    }, delayMs);
  }

  private pollForGeneratedAnalysis(): void {
    if (!this.searchId || !this.type) {
      this.stopPolling();
      return;
    }

    this.pollAttempts += 1;

    this.osintSearchService.getAiAnalysis(this.type, this.searchId, this.analysisMode).subscribe({
      next: (response) => {
        this.analysisResponse = response?.data || null;
        this.syncFeedbackDraftFromResponse();
        const status = String(this.analysisResponse?.status || '').toLowerCase();
        const hasAnalysis = Boolean(this.analysisResponse?.analysis);

        if (hasAnalysis || status === 'ready') {
          this.errorMessage = '';
          this.stopPolling();
          return;
        }

        if (status === 'failed' && this.analysisResponse?.error) {
          this.errorMessage = this.analysisResponse.error;
          this.stopPolling();
          return;
        }

        if (this.pollAttempts >= this.maxPollAttempts) {
          this.errorMessage =
            'AI analysis is still processing on the server. Please refresh in a moment.';
          this.stopPolling();
          return;
        }

        this.scheduleNextPoll();
      },
      error: () => {
        if (this.pollAttempts >= this.maxPollAttempts) {
          this.errorMessage =
            'AI analysis is still processing on the server. Please refresh in a moment.';
          this.stopPolling();
          return;
        }

        this.scheduleNextPoll();
      },
    });
  }

  private stopPolling(): void {
    this.clearPollTimer();
    this.isPolling = false;
    this.pollAttempts = 0;
  }

  private clearPollTimer(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private syncFeedbackDraftFromResponse(): void {
    if (!this.canUseFeedback) {
      this.resetFeedbackDraft();
      return;
    }

    const feedback = this.analysisResponse?.analyst_feedback;
    this.feedbackStatus =
      feedback?.status === 'wrong' ||
      feedback?.status === 'correct' ||
      feedback?.status === 'needs-review'
        ? feedback.status
        : '';
    this.feedbackNote = typeof feedback?.note === 'string' ? feedback.note : '';
  }

  private resetFeedbackDraft(): void {
    this.feedbackStatus = '';
    this.feedbackNote = '';
  }
}
